// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { type Person } from '@/account/api/types';
import { formatPersonName } from '@/account/utilities/format-person-name';
import { phaseDateSubtext } from '@/components/phase-date-subtext';
import { type Phase, PhaseIndicator } from '@/components/phase-indicator';
import {
  FULFILLMENT_ORDER_CLOSED_AT_SOURCE_LABELS,
  type FulfillmentOrderClosedAtSource,
  type FulfillmentOrderPhase,
  type FulfillmentOrderStatus,
  PHASE_CONFIG,
  getPhaseProgression,
  getTerminalPhases,
  parseStatus,
} from '@/fulfillment/constants/fulfillment-order-status';
import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

type FulfillmentOrderDisplayPhase = FulfillmentOrderPhase | 'closed';

interface Props {
  currentStatus: FulfillmentOrderStatus;
  originLocationIsThirdPartyEnabled: boolean;
  isThirdPartyFulfilled: boolean;
  cancelledReason?: string | null;
  createdAt: DateTime;
  openedAt?: DateTime | null;
  shippedAt?: DateTime | null;
  deliveredAt?: DateTime | null;
  closedAt?: DateTime | null;
  closedAtSource?: FulfillmentOrderClosedAtSource | null;
  closedBy?: Person | null;
}

/**
 * Component that displays the current phase of a fulfillment order.
 * Shows a stepper-style indicator with phases based on 3PL status.
 * With 3PL: Draft → Awaiting 3PL → Open → Shipped → Delivered → Closed/Cancelled
 * Without 3PL: Draft → Open → Shipped → Delivered → Closed/Cancelled
 */
export function FulfillmentPhaseIndicator(props: Props) {
  const {
    currentStatus,
    originLocationIsThirdPartyEnabled,
    isThirdPartyFulfilled,
    cancelledReason,
    createdAt,
    openedAt,
    shippedAt,
    deliveredAt,
    closedAt,
    closedAtSource,
    closedBy,
  } = props;

  const { phase: baseStatus, modifier } = parseStatus(currentStatus);

  // Determine which workflow to use:
  // - For DRAFT: use origin location's 3PL setting
  // - For non-DRAFT: use the FO's isThirdPartyFulfilled flag
  const uses3PLWorkflow =
    currentStatus === 'DRAFT'
      ? originLocationIsThirdPartyEnabled
      : isThirdPartyFulfilled;
  const showClosedStep =
    closedAt !== null &&
    closedAt !== undefined &&
    closedAtSource !== 'CANCELLED';
  const showCancelledStep = currentStatus === 'CANCELLED';

  const phases = useMemo(() => {
    const phaseDates: Partial<
      Record<FulfillmentOrderDisplayPhase, DateTime | null>
    > = {
      draft: createdAt,
      open: openedAt,
      shipped: shippedAt,
      delivered: deliveredAt,
    };

    const workflowPhases = getPhaseProgression(uses3PLWorkflow).map(
      (status, index): Phase<FulfillmentOrderDisplayPhase> => ({
        status,
        label:
          status === baseStatus && modifier === 'partial'
            ? `Partially ${PHASE_CONFIG[status].label.toLowerCase()}`
            : PHASE_CONFIG[status].label,
        number: index + 1,
        isPartial: status === baseStatus && modifier === 'partial',
        subtext: phaseDateSubtext(phaseDates[status]),
      })
    );

    if (showCancelledStep) {
      workflowPhases.push({
        status: 'cancelled',
        label: PHASE_CONFIG.cancelled.label,
        number: workflowPhases.length + 1,
      });
      return workflowPhases;
    }

    if (!showClosedStep) {
      return workflowPhases;
    }

    workflowPhases.push({
      status: 'closed',
      label: 'Closed',
      number: workflowPhases.length + 1,
      subtext: phaseDateSubtext(closedAt),
      tooltip: (
        <Stack spacing={0.5}>
          <Typography variant="caption">
            {getClosedAtSourceLabel(closedAtSource)}
          </Typography>
          <Typography variant="caption">
            Closed at {closedAt.toLocaleString(DateTime.DATETIME_MED)}
          </Typography>
          {closedBy && (
            <Typography variant="caption">
              Closed by {formatPersonName(closedBy)}
            </Typography>
          )}
        </Stack>
      ),
    });

    return workflowPhases;
  }, [
    uses3PLWorkflow,
    baseStatus,
    modifier,
    createdAt,
    openedAt,
    shippedAt,
    deliveredAt,
    closedAt,
    closedAtSource,
    closedBy,
    showClosedStep,
    showCancelledStep,
  ]);

  const completedStatuses = useMemo(() => {
    if (!showClosedStep && !showCancelledStep) return undefined;

    const workflowPhases = getPhaseProgression(uses3PLWorkflow);
    const reachedPhase = showCancelledStep
      ? getCancelledReachedPhase(isThirdPartyFulfilled)
      : baseStatus;
    const reachedIndex = workflowPhases.indexOf(reachedPhase);
    const completedWorkflowPhases =
      reachedIndex === -1 ? [] : workflowPhases.slice(0, reachedIndex + 1);

    return [
      ...completedWorkflowPhases,
      showCancelledStep ? 'cancelled' : 'closed',
    ];
  }, [
    uses3PLWorkflow,
    baseStatus,
    isThirdPartyFulfilled,
    showClosedStep,
    showCancelledStep,
  ]);

  const currentPhase: FulfillmentOrderDisplayPhase = showClosedStep
    ? 'closed'
    : showCancelledStep
      ? 'cancelled'
      : baseStatus;

  return (
    <PhaseIndicator
      phases={phases}
      currentStatus={currentPhase}
      completedStatuses={completedStatuses}
      cancelledStatus="cancelled"
      terminalStatuses={[...getTerminalPhases(), 'closed']}
      cancelledReason={cancelledReason}
    />
  );
}

function getClosedAtSourceLabel(
  source: FulfillmentOrderClosedAtSource | null | undefined
): string {
  if (!source) return 'Closed';
  return FULFILLMENT_ORDER_CLOSED_AT_SOURCE_LABELS[source];
}

function getCancelledReachedPhase(
  isThirdPartyFulfilled: boolean
): FulfillmentOrderPhase {
  return isThirdPartyFulfilled ? 'awaiting_3pl' : 'draft';
}
